/* AggregatedHealthScoresCard — обединена секция с резултати от
 * всички "вертикални" модули на платформата (iter421).
 *
 * Източници (всички от latest assessment на потребителя):
 *   • Biological Age (Levine PhenoAge)
 *   • Longevity Score
 *   • Dental Risk
 *   • Breast Cancer Risk (Gail + GLOBOCAN BG)
 *   • Inflammation Index (UIS) — от последен Plate Simulator snapshot
 *   • Probable Phenotype — правило-базиран агрегатор
 *
 * Тон: всичко е "област за подобрение" / "заслужава проследяване".
 * Без думи "риск", "опасност", "смърт".
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Activity, TrendingUp, Sparkles, Flame, Heart, Smile, Brain, Loader2,
  ArrowRight, Droplets, Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

function ScoreTile({ icon: Icon, label, value, unit, hint, color, isDark, linkTo, linkLabel, testId, empty }) {
  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"} overflow-hidden`}
          data-testid={testId}>
      <CardContent className="p-3.5">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center bg-gradient-to-br ${color} text-white`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className={`text-[10.5px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {label}
          </div>
        </div>
        {empty ? (
          <div>
            <div className={`text-xs ${isDark ? "text-slate-500" : "text-slate-500"} mb-2`}>
              {hint}
            </div>
            {linkTo && (
              <Link to={linkTo}>
                <Button variant="outline" size="sm" className="text-[10.5px] h-7">
                  {linkLabel}
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-1.5">
              <span className={`text-2xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                {value}
              </span>
              {unit && (
                <span className={`text-xs font-medium ${isDark ? "text-slate-500" : "text-slate-500"}`}>{unit}</span>
              )}
            </div>
            {hint && (
              <p className={`text-[10.5px] mt-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>{hint}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function AggregatedHealthScoresCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/health-intelligence/aggregated-scores`);
      setData(r.data);
    } catch (_e) {
      /* mute */
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className={`w-5 h-5 animate-spin ${isDark ? "text-violet-400" : "text-violet-600"}`} />
      </div>
    );
  }
  if (!data) return null;
  const s = data.scores || {};

  // Phenotype banner
  const phen = s.probable_phenotype || {};
  const phenLabel = bg ? phen.label_bg : phen.label_en;

  return (
    <div className="space-y-3" data-testid="aggregated-scores">
      {/* Phenotype banner */}
      {phenLabel && (
        <Card className={`border ${isDark ? "border-violet-500/30 bg-violet-500/10" : "border-violet-200 bg-violet-50/60"}`}
              data-testid="probable-phenotype-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex-shrink-0`}>
                <Brain className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={`text-sm font-bold ${isDark ? "text-violet-100" : "text-violet-900"}`}>
                    {bg ? "Вероятен метаболитен фенотип" : "Probable metabolic phenotype"}
                  </h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                        isDark ? "bg-violet-500/30 text-violet-200" : "bg-violet-600 text-white"
                      }`}>
                    {phen.confidence_pct}% {bg ? "сигурност" : "confidence"}
                  </span>
                </div>
                <p className={`text-base font-extrabold mt-0.5 ${isDark ? "text-white" : "text-slate-900"}`}>
                  {phenLabel}
                </p>
                {phen.contributors_bg && phen.contributors_bg.length > 0 && (
                  <ul className={`text-[11.5px] mt-2 space-y-0.5 leading-relaxed ${isDark ? "text-violet-200/80" : "text-violet-900/80"}`}>
                    {phen.contributors_bg.slice(0, 4).map((c, i) => (
                      <li key={i}>• {c}</li>
                    ))}
                  </ul>
                )}
                <p className={`text-[10px] mt-2 italic ${isDark ? "text-violet-300/60" : "text-violet-700/60"}`}>
                  {bg ? phen.method_note_bg : "Rule-based aggregation from foods, conditions, body & biomarkers."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scores grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <ScoreTile
          icon={Sparkles} label={bg ? "Биологична възраст" : "Biological age"}
          value={s.biological_age?.phenoage_years != null ? Math.round(s.biological_age.phenoage_years * 10) / 10 : null}
          unit={bg ? "г" : "yrs"}
          color="from-violet-500 to-fuchsia-500"
          hint={
            s.biological_age?.delta_years != null
              ? (s.biological_age.delta_years < 0
                  ? `${Math.abs(s.biological_age.delta_years).toFixed(1)} ${bg ? "г. по-млад от хронологичната" : "yrs younger than chronological"}`
                  : `+${s.biological_age.delta_years.toFixed(1)} ${bg ? "г. над хронологичната — област за подобрение" : "yrs above chronological — area to improve"}`)
              : (bg ? "PhenoAge — Levine 2018" : "PhenoAge — Levine 2018")
          }
          isDark={isDark}
          empty={!s.biological_age?.phenoage_years}
          linkTo="/dashboard/analyses"
          linkLabel={bg ? "Изчисли" : "Calculate"}
          testId="agg-score-phenoage"
        />
        <ScoreTile
          icon={TrendingUp} label={bg ? "Дълголетие" : "Longevity"}
          value={s.longevity?.score != null ? Math.round(s.longevity.score) : null}
          unit="/100"
          color="from-emerald-500 to-teal-500"
          hint={s.longevity?.band || (bg ? "Levine + биомаркери" : "Levine + biomarkers")}
          isDark={isDark}
          empty={s.longevity?.score == null}
          linkTo="/dashboard/analyses"
          linkLabel={bg ? "Изчисли" : "Calculate"}
          testId="agg-score-longevity"
        />
        <ScoreTile
          icon={Smile} label={bg ? "Дентална оценка" : "Dental score"}
          value={s.dental?.score != null ? Math.round(s.dental.score) : null}
          unit=""
          color="from-sky-500 to-cyan-500"
          hint={s.dental?.band || (bg ? "Заслужава проследяване" : "Worth tracking")}
          isDark={isDark}
          empty={s.dental?.score == null}
          linkTo="/health-intelligence/dental"
          linkLabel={bg ? "Отвори DHI" : "Open DHI"}
          testId="agg-score-dental"
        />
        <ScoreTile
          icon={Droplets} label={bg ? "Бъбречна оценка" : "Kidney score"}
          value={s.kidney?.score != null ? Math.round(s.kidney.score) : null}
          unit="/100"
          color="from-cyan-500 to-blue-500"
          hint={bg ? "Филтрация, лимити и проследяване (KHI)" : "Filtration & limits (KHI)"}
          isDark={isDark}
          empty={s.kidney?.score == null}
          linkTo="/dashboard/kidney-intelligence"
          linkLabel={bg ? "Отвори KHI" : "Open KHI"}
          testId="agg-score-kidney"
        />
        <ScoreTile
          icon={Zap} label={bg ? "Кортизолов товар" : "Cortisol load"}
          value={s.cortisol?.load != null ? Math.round(s.cortisol.load) : null}
          unit="/100"
          color="from-orange-500 to-amber-500"
          hint={s.cortisol?.program_day
            ? (bg ? `Ден ${s.cortisol.program_day} от 28 · ${s.cortisol.program_percent}% изпълнение`
                  : `Day ${s.cortisol.program_day} of 28`)
            : (s.cortisol?.band || (bg ? "Запишете стрес за оценка" : "Log stress"))}
          isDark={isDark}
          empty={s.cortisol?.load == null}
          linkTo="/dashboard/stress-twin"
          linkLabel={bg ? "Кортизол" : "Cortisol"}
          testId="agg-score-cortisol"
        />
        <ScoreTile
          icon={Heart} label={bg ? "Риск от рак на гърдата (10г)" : "Breast cancer score (10y)"}
          value={s.breast_risk?.ten_year_pct != null ? `${(s.breast_risk.ten_year_pct).toFixed(1)}` : null}
          unit="%"
          color="from-rose-500 to-pink-500"
          hint={s.breast_risk?.method ? `${bg ? "10 год.:  / Lifetime" : "Lifetime"} ${s.breast_risk.lifetime_pct != null ? s.breast_risk.lifetime_pct.toFixed(1) + "%" : "—"}` : (bg ? "Заслужава проследяване" : "Worth tracking")}
          isDark={isDark}
          empty={s.breast_risk?.ten_year_pct == null}
          linkTo="/health-intelligence/breast"
          linkLabel={bg ? "Оцени" : "Assess"}
          testId="agg-score-breast"
        />
        <ScoreTile
          icon={Flame} label={bg ? "Общ Индекс Възпаление" : "Inflammation Index"}
          value={s.inflammation?.score_pct != null ? Math.round(s.inflammation.score_pct) : null}
          unit="%"
          color="from-amber-500 to-orange-500"
          hint={bg ? "От последен анализ на чиния" : "From latest plate analysis"}
          isDark={isDark}
          empty={s.inflammation?.score_pct == null}
          linkTo="/dashboard/diary#plate-simulator-anchor"
          linkLabel={bg ? "Анализирай чиния" : "Analyze plate"}
          testId="agg-score-inflammation"
        />
        {/* Calculators — shows actual saved results when available */}
        <CalculatorsScoreTile scores={s} isDark={isDark} bg={bg} />
      </div>

      <p className={`text-[10.5px] italic ${isDark ? "text-slate-500" : "text-slate-500"}`}>
        {bg ? data.disclaimer_bg : data.disclaimer_en}
      </p>
    </div>
  );
}

// iter421.7 — Calculators tile shows latest BMI/HOMA-IR/eGFR/CVD when available
function CalculatorsScoreTile({ scores, isDark, bg }) {
  const calcs = scores.calculators || {};
  const items = [];
  if (calcs.bmi)          items.push({ label: "BMI",       value: calcs.bmi.score,      band: calcs.bmi.label_bg });
  if (calcs.homa_ir)      items.push({ label: "HOMA-IR",   value: calcs.homa_ir.score,  band: calcs.homa_ir.label_bg });
  if (calcs.egfr)         items.push({ label: "eGFR",      value: calcs.egfr.score,     band: calcs.egfr.label_bg });
  if (calcs.cvd_risk)     items.push({ label: bg ? "CVD 10г" : "CVD 10y", value: calcs.cvd_risk.score,     band: calcs.cvd_risk.label_bg });
  if (calcs.diabetes_risk) items.push({ label: bg ? "Диабет" : "Diabetes", value: calcs.diabetes_risk.score, band: calcs.diabetes_risk.label_bg });

  if (items.length === 0) {
    return (
      <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"} overflow-hidden`}
            data-testid="agg-score-calculators">
        <CardContent className="p-3.5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-gradient-to-br from-indigo-500 to-violet-500 text-white">
              <Activity className="w-4 h-4" />
            </div>
            <div className={`text-[10.5px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {bg ? "Калкулатори" : "Calculators"}
            </div>
          </div>
          <p className={`text-[10.5px] mb-2 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {bg ? "HOMA-IR · eGFR · BMI · CVD · FINDRISC" : "HOMA-IR · eGFR · BMI · CVD · FINDRISC"}
          </p>
          <Link to="/dashboard/analyses">
            <Button variant="outline" size="sm" className="text-[10.5px] h-7">
              {bg ? "Отвори" : "Open"} <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"} overflow-hidden`}
          data-testid="agg-score-calculators">
      <CardContent className="p-3.5">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-gradient-to-br from-indigo-500 to-violet-500 text-white">
            <Activity className="w-4 h-4" />
          </div>
          <div className={`text-[10.5px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg ? "Калкулатори" : "Calculators"}
          </div>
        </div>
        <ul className="space-y-1">
          {items.slice(0, 5).map((it, i) => (
            <li key={i} className={`text-[11px] flex items-center justify-between ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              <span className="font-semibold opacity-80">{it.label}</span>
              <span className="font-bold">{it.value != null ? (typeof it.value === "number" ? it.value.toFixed(it.label === "HOMA-IR" ? 2 : 1) : it.value) : "—"}</span>
              {it.band && <span className={`text-[9.5px] px-1.5 rounded-full ${isDark ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-600"}`}>{it.band}</span>}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
